import React, { useContext } from 'react';
import Box from "@mui/material/Box";
import { PieChart } from "@mui/x-charts";
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { UserContext } from '../../../../context/UserContext';
import { percentageFormat } from '../../../../helpers/helpers';


const scrollCSS = {
    "&::-webkit-scrollbar": {
        width: "5px",
    },
    "&::-webkit-scrollbar-track": {
        background: "#f1f1f1",
        borderRadius: "10px",
    },
    "&::-webkit-scrollbar-thumb": {
        background: "#888",
        borderRadius: "10px",
    },
    "&::-webkit-scrollbar-thumb:hover": {
        background: "#555",
    },
}

interface Colour {
    front: string;
    back: string;
}

export interface EposNowPieChartData {
    value: number;
    label: string;
}

export interface EposNowPieChartProps {
    data: EposNowPieChartData[],
    height?: number;
}

const EposNowPieChart = (props: EposNowPieChartProps) => {
    const {
        data,
        height
    } = props;
    const theme = useTheme();
    const { locale } = useContext(UserContext);
    const colours: Colour[] = [
        {
            front: theme.palette.blue.main,
            back: theme.palette.blue.light
        },
        {
            front: theme.palette.cyan.main,
            back: theme.palette.cyan.light
        },
        {
            front: theme.palette.teal.main,
            back: theme.palette.teal.light
        },
        {
            front: theme.palette.lightGreen.main,
            back: theme.palette.lightGreen.light
        },
        {
            front: theme.palette.lime.main,
            back: theme.palette.lime.light
        },
        {
            front: theme.palette.yellow.main,
            back: theme.palette.yellow.light
        },
        {
            front: theme.palette.orange.main,
            back: theme.palette.orange.light
        },
        {
            front: theme.palette.deepOrange.main,
            back: theme.palette.deepOrange.light
        },
        {
            front: theme.palette.fucsia.main,
            back: theme.palette.fucsia.light
        },
        {
            front: theme.palette.purple.main,
            back: theme.palette.purple.light
        }
    ];
    const getFrontColour = (index: number) => colours[index % colours.length].front;
    const total = data?.reduce(((x, y) => x+y.value), 0) || 1;
    data?.sort((a, b) => b.value - a.value);
    
    return (
        <Box sx={{ height, display: 'flex', flexDirection: 'column', overflow: 'hidden', breakInside: 'avoid',
          pageBreakInside: 'avoid' }}>
            <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent:'flex-start' }}>
                <Box sx={{ width: '150px', height: '150px' }}>
                    <PieChart
                        colors={colours.map(c => c.front)}
                        width={150}
                        height={150}
                        slotProps={{ legend: {hidden: true} }}
                        tooltip={{ trigger: 'none' }}
                        series={[{
                            innerRadius: '50%',
                            outerRadius: '100%',
                            startAngle: 20,
                            endAngle: 380,
                            paddingAngle: 0,
                            data,
                        }]}
                        margin={{ right: 0 }}
                    />
                </Box>
            </Box>
            <Box sx={{ overflow: 'auto', marginTop: '15px', ...scrollCSS}}>
                {data?.map((item, index) => (
                    <Box key={`${item.label}-${item.value}`} sx={{ display: 'flex', flexDirection: 'row', width: 'auto', justifyContent:'space-between', margin: '6px 10px 6px 6px' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                            <Box
                                sx={{
                                    width: '16px',
                                    height: '16px',
                                    borderRadius: '50%',
                                    bgcolor: getFrontColour(index),
                                    marginRight: '8px',
                                }}
                            />
                            <Typography style={theme.typography.pieBarText}>{item.label}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'row', width: 'auto', marginLeft: '8px' }}>
                            <Typography variant='body1'>
                                {percentageFormat(item.value/total, locale)}
                            </Typography>
                        </Box>
                    </Box>
                ))}
            </Box>
        </Box>
    )
}

export default EposNowPieChart;
